// Session-breadcrumb relay between the app and the Chrome extension side panel.
//
// WHY THIS EXISTS
// The real credential is the `sessionId` COOKIE (axios withCredentials,
// services/api.js), and it survives the side panel being closed — Chromium treats
// an extension-embedded iframe as first-party while the manifest holds
// host_permissions for its origin. What does NOT survive is the client-side
// "am I logged in" breadcrumb: login() parks it in sessionStorage when remember-me
// is off (the default), and sessionStorage dies with the panel document. On reopen
// checkAutoLogin finds nothing, never asks the server, and a perfectly live session
// renders as a login screen.
//
// So the breadcrumb is kept by the panel itself, in chrome.storage.session
// (extension-scoped, memory-backed, gone when the browser fully quits — see
// chrome-app/sidepanel/sidepanel.js), and handed back here over postMessage.
// Nothing durable is written on either side, so remember-me-off keeps meaning
// "until the browser closes" rather than quietly becoming persistent on a shared
// clinic workstation.
//
// TRUST BOUNDARY — the reason this file is its own module
// The breadcrumb is not inert: the raw session identifier is accepted as a
// `session-id` header by the twilio sidecar, so posting it to the wrong parent is a
// credential leak, not a cosmetic bug. Hence:
//   * nothing is ever sent until a handshake has been VALIDATED (we are framed, the
//     sender IS our parent, and its origin carries the chrome-extension: scheme);
//   * the reply origin is PINNED from that validated handshake, and every outbound
//     message uses it as an exact targetOrigin — never "*", and never a value read
//     out of a message body;
//   * with no handshake there is no pinned origin, so the outbound path is
//     unreachable by construction. The native shell (window.ReactNativeWebView, where
//     the app is top-level) and the white-label embeds never send one, which is what
//     makes every path in this file inert for them.
//
// This is deliberately NOT wired into hooks/usePostMessages.js: that hook is the
// NATIVE bridge and accepts messages on both window and document with no origin
// check at all. Adding an origin-checked channel there would mean either loosening
// this one to match it or making that file mean two different things.
//
// KNOWN LIMITATION — impersonation resurrection without a banner (documented,
// not fixed here; council iter1 REMAND ruling, no cheap code fix exists)
// The impersonation session cookie is browser-wide (utils/impersonation.js's own
// header comment says so), but the banner markers (IMPERSONATOR_KEY / SESSION_KEY)
// live in sessionStorage and die with the panel document. So: an operator who logs
// in normally inside the panel (parking a breadcrumb here via login()) and then
// impersonates a user from that same browser will, on panel reopen, have the
// relayed breadcrumb restore the IMPERSONATED session — with no banner and no
// working Exit control, because sessionStorage's markers are gone. Today the
// missing breadcrumb is what prevents this.
// The obvious one-line fix — calling sendSessionClear() from the impersonation
// handover — does NOT work: that handover runs in a fresh top-level tab
// (`window.parent === window`), where this bridge has no pinned reply origin, so
// the call is a silent no-op. A real fix needs the impersonation handover to reach
// into the SAME panel document (it doesn't — impersonation opens a new top-level
// tab), which is out of scope for this module. This class already exists on
// `stage` today via checkAutoLogin's own localStorage read for a remember-me
// operator; this relay widens the surface (any panel login, not just remember-me)
// without creating the underlying gap. See BUILD_LOG.md and follow-up FU-1.
//
// INVARIANT (added iter1 fix pass, council REMAND) — at most one auth bootstrap
// per document load. checkAutoLogin (useAuth.js) already owns the case where a
// breadcrumb survived in sessionStorage/localStorage: it fires on mount and starts
// its own checkAuth before the panel's handshake can possibly arrive. If
// restoreRelayedSession ALSO ran in that case, it would start a second, concurrent
// checkAuth bootstrap — duplicate profile/org/plan fetches, a duplicate PHI roster
// snapshot, and a duplicate api.updateProfile server write via
// checkLocation -> changeLocation — every time a remember-me user reopens the
// panel, or any user does a full reload inside it (sessionStorage survives an
// in-frame reload). restoreRelayedSession enforces the invariant by refusing to
// run at all when either storage already holds a breadcrumb; see its own comment
// below. The case it still must handle — and does — is the panel opening fresh
// with NEITHER storage populated, where checkAutoLogin found nothing and only the
// relay can restore the session.

export const EXTENSION_HANDSHAKE_TYPE = "lucasExtensionHandshake";
export const EXTENSION_SESSION_UPDATE_TYPE = "lucasExtensionSessionUpdate";
export const EXTENSION_SESSION_CLEAR_TYPE = "lucasExtensionSessionClear";

const EXTENSION_ORIGIN_PROTOCOL = "chrome-extension:";

/**
 * True when `origin` names an extension page.
 *
 * Parsed, never substring-matched: "https://chrome-extension.evil.com" contains the
 * scheme text and must fail.
 */
export function isExtensionOrigin(origin) {
  if (typeof origin !== "string" || origin === "") return false;
  try {
    return new URL(origin).protocol === EXTENSION_ORIGIN_PROTOCOL;
  } catch (e) {
    // Opaque origins arrive as the literal "null" and are unparseable — rejected.
    return false;
  }
}

/**
 * The three-part gate for an inbound handshake. All three parts are required:
 *   - `win.parent !== win`: unframed, there is no panel. Without this check our own
 *     postMessage to ourselves would qualify as a handshake.
 *   - `event.source === win.parent`: any frame can post to us; only the embedder
 *     may claim to be the panel.
 *   - chrome-extension: origin: a page on the web can be framed inside another page
 *     too, and must never be able to pin itself as our reply target.
 */
export function isValidExtensionHandshake(event, win) {
  if (!event || !win) return false;
  if (!win.parent || win.parent === win) return false;
  if (event.source !== win.parent) return false;
  if (!isExtensionOrigin(event.origin)) return false;
  const data = event.data;
  return !!data && typeof data === "object" && data.type === EXTENSION_HANDSHAKE_TYPE;
}

/** The relayed breadcrumb, or null when the panel had none to offer. */
export function readHandshakeSessionId(event) {
  const sessionId = event?.data?.sessionId;
  return typeof sessionId === "string" && sessionId !== "" ? sessionId : null;
}

/**
 * Seed the relayed breadcrumb and run the normal cookie-authenticated check once.
 *
 * This exists because the relayed restore routinely LOSES the race with
 * checkAutoLogin: Routes.js fires that on mount, before the panel's handshake
 * arrives, and it returns without asking the server because there is no breadcrumb
 * yet. Re-running checkAuth from here is what makes the restore work regardless of
 * which of the two lands first.
 *
 * But checkAutoLogin can also WIN that race — a remember-me user's localStorage
 * breadcrumb, or a surviving sessionStorage one from an in-frame reload, is found
 * on mount and its own checkAuth(true) is already in flight (or already finished)
 * by the time the handshake lands. `isAuthenticated` alone doesn't see that: it's
 * only true once checkAuth's two sequential round trips have both resolved and
 * `setAuth` has run, so every handshake that arrives before then would otherwise
 * start a SECOND, concurrent bootstrap for the exact same identity. checkAutoLogin
 * already owns "a breadcrumb survived" — restoring here too is not a race with it,
 * it's a duplicate of it, complete with a duplicate `api.updateProfile` write (via
 * checkAuth -> checkLocation -> changeLocation) and a duplicate PHI roster
 * snapshot. So: if either storage already holds a breadcrumb when the handshake
 * arrives, skip — checkAutoLogin has it. Only a genuinely empty pair (the panel
 * opening fresh, nothing for checkAutoLogin to have found) reaches the seed-and-
 * checkAuth path below.
 *
 * Exactly ONE attempt, and a silent failure. A dead cookie means the login screen,
 * which is the correct answer rather than an error to report — same posture (and the
 * same Sentry-noise reasoning) as checkAutoLogin's swallowed rejection. The seed is
 * rolled back on failure so storage is left exactly as the relay found it — and only
 * if it still holds the exact value THIS call wrote, so a rollback can never clobber
 * a session someone else established in the meantime.
 *
 * The breadcrumb stays a LOCAL gate: it is written to storage and handed to
 * checkAuth as its existing `loginSessionId` argument, and is never attached to a
 * request.
 */
export async function restoreRelayedSession({ sessionId, isAuthenticated, checkAuth, storage, localStorage: localStore } = {}) {
  if (typeof sessionId !== "string" || sessionId === "") return false;
  if (isAuthenticated) return false;
  if (typeof checkAuth !== "function") return false;

  const store = storage || (typeof window !== "undefined" ? window.sessionStorage : null);
  const local = localStore || (typeof window !== "undefined" ? window.localStorage : null);

  // At-most-one-bootstrap invariant (see the file-level comment above): a
  // breadcrumb already present in EITHER storage means checkAutoLogin already
  // claimed this restore, so this call must not also run one.
  let existingBreadcrumb = null;
  try {
    existingBreadcrumb = store?.getItem?.("sessionId") || local?.getItem?.("sessionId") || null;
  } catch (e) {
    existingBreadcrumb = null;
  }
  if (existingBreadcrumb) return false;

  try {
    store?.setItem("sessionId", sessionId);
  } catch (e) {
    // Private mode / quota. The cookie is the credential, so still try the call —
    // checkAuth takes the id as an argument too.
  }

  try {
    await checkAuth(true, sessionId);
    return true;
  } catch (e) {
    try {
      if (store?.getItem?.("sessionId") === sessionId) store.removeItem("sessionId");
    } catch (_e) {}
    return false;
  }
}

/**
 * Build a bridge bound to `win` (defaults to the real window).
 *
 * Factory rather than bare module state so tests get an isolated instance; the app
 * uses the single `extensionSessionBridge` export below.
 */
export function createExtensionSessionBridge({ win } = {}) {
  // Only ever set to the parent's browser-attested `event.origin` from a validated
  // handshake — never a value read out of a message body, never "*". Null means "no
  // handshake yet", which is what makes every send a no-op.
  //
  // NOTE: this DOES get reassigned on every validated handshake, not just the
  // first (see `replyOrigin = event.origin` in `install` below). That re-pin can
  // never hand the slot to a different origin though: `isValidExtensionHandshake`
  // requires `event.source === win.parent`, a document has exactly one parent, and
  // `event.origin` is browser-attested for that one sender — so every re-pin can
  // only ever write back the same value. Do not read this as "pinned once and
  // locked"; read it as "always exactly the validated parent's origin, or null".
  let replyOrigin = null;
  let restoreOffered = false;

  const target = () => win || (typeof window !== "undefined" ? window : null);

  const post = (message) => {
    const w = target();
    if (!w || !replyOrigin) return false;
    try {
      w.parent.postMessage(message, replyOrigin);
      return true;
    } catch (e) {
      return false;
    }
  };

  return {
    isInExtensionPanel: () => replyOrigin !== null,
    getReplyOrigin: () => replyOrigin,

    /**
     * Start listening for the panel's handshake; returns the uninstall function.
     *
     * `onRestore(sessionId)` runs at most once for the life of the bridge no matter
     * how many handshakes arrive — the panel re-sends on a bounded schedule because
     * it cannot know when React mounted this listener, and a second restore attempt
     * on a dead cookie is exactly the retry loop that must not happen.
     */
    install(onRestore) {
      const w = target();
      if (!w) return () => {};

      const handleMessage = (event) => {
        if (!isValidExtensionHandshake(event, w)) return;
        replyOrigin = event.origin;

        const sessionId = readHandshakeSessionId(event);
        if (!sessionId || restoreOffered) return;
        restoreOffered = true;
        onRestore(sessionId);
      };

      w.addEventListener("message", handleMessage);
      return () => w.removeEventListener("message", handleMessage);
    },

    sendSessionUpdate(sessionId) {
      if (typeof sessionId !== "string" || sessionId === "") return false;
      return post({ type: EXTENSION_SESSION_UPDATE_TYPE, sessionId });
    },

    sendSessionClear() {
      return post({ type: EXTENSION_SESSION_CLEAR_TYPE });
    },
  };
}

export const extensionSessionBridge = createExtensionSessionBridge();
